import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import { Upload } from "lucide-react";
import PropTypes from "prop-types";

import DataTable from "../DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { getDatum } from "../../services/formatService.js";
import { usePaymentImportActions } from "../../hooks/usePaymentImportActions.jsx";
import PaymentImportStatusBadge from "../PaymentImportStatusBadge.jsx";
import { useSearchParams } from "react-router-dom";

export default function PaymentImportsTable({
  onRowClick = () => {},
  onClickUpload = () => {},
  onChangeTableState = () => {},
  tableConfig = {}
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const { state: paymentImportState, setState: setPaymentImportState } =
    usePaymentImportActions();

  const tableKey = "payment_imports";
  const storageKey = "v2_table_payment_imports";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const rows = paymentImportState?.data?.data || [];

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Payment Imports",
        route: "data/payment-imports",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams: {
          sort: [{ field: "from_date", order: "desc" }],
        },
        onStateChange: setPaymentImportState,
        ...tableConfig,
      },
    ],
    [defaultRowsPerPage, storageKey, tableConfig]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    onChangeTableState(tableState);
  }, [tableState.sorting, tableState.pageIndex, tableState.filters, tableState.searchword]);

  useEffect(() => {
    fetchData(tableKey);
  }, []);

  const toolbarActions = [
    {
      type: "button",
      label: "Datei hochladen",
      icon: <Upload size={16} />,
      onClick: onClickUpload,
      variant: "primary",
    },
  ];

  const columns = [
    { accessorKey: "file_name", header: "Datei" },
    {
      accessorKey: "from_date",
      sortDescFirst: false,
      header: "Von",
      size: 60,
      Cell: ({ cell }) => {
        if (!cell) {
          return null;
        }
        return getDatum(cell.getValue());
      },
    },
    {
      accessorKey: "to_date",
      sortDescFirst: false,
      header: "Bis",
      size: 60,
      Cell: ({ cell }) => {
        if (!cell) {
          return null;
        }
        return getDatum(cell.getValue());
      },
    },
    {
      accessorKey: "processed_records",
      enableColumnFilter: false,
      header: "Importiert",
      size: 20,
      minSize: 20,
      maxSize: 80,
    },
    {
      accessorKey: "assigned_records",
      enableColumnFilter: false,
      header: "Zugeordnet",
      size: 20,
    },
    {
      accessorKey: "failed_records",
      enableColumnFilter: false,
      header: "Fehlerhaft",
      size: 20,
    },
    {
      accessorKey: "created_at",
      header: "Erstellt am",
      size: 100,
      Cell: ({ cell }) => {
        if (!cell) {
          return null;
        }
        const date = new Date(cell.getValue());
        return new Intl.DateTimeFormat("de-DE", {
          dateStyle: "short",
          timeStyle: "short",
        }).format(date);
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 80,
      Cell: ({ cell }) => {
        if (!cell) {
          return null;
        }
        const status = cell.getValue();

        return <PaymentImportStatusBadge status={status} />;
      },
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangePage={(pagination) => {
        const next =
          typeof pagination === "function"
            ? pagination({
                pageIndex: tableState.pageIndex,
                pageSize: tableState.pageSize,
              })
            : pagination;
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      onRowClick={onRowClick}
      enableHeader
      enableSorting
      manualFiltering={true}
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      toolbarActions={toolbarActions}
      initialState={{
        globalFilter: tableState.searchword
      }}
    />
  );
}

PaymentImportsTable.propTypes = {
  onRowClick: PropTypes.func,
  onClickUpload: PropTypes.func,
};
